/**
 * Google Analytics 4, loaded only when a measurement id is configured.
 *
 * GA4 covers the anonymous top of the funnel — visits, geography, referrer, campaign parameters,
 * landing pages and the path through the site. It deliberately does *not* carry the signup or
 * product events: those go server-side to PostHog, keyed to the account id, because GA4 cannot put
 * a row next to a user in our own database and is eaten by ad blockers and in-app browsers — which
 * is most of the social traffic this is being added to measure.
 */

type GtagArguments = [string, ...unknown[]];

declare global {
	interface Window {
		dataLayer?: unknown[];
		gtag?: (...args: GtagArguments) => void;
		// GA's documented opt-out switch: window["ga-disable-<measurement id>"] = true.
		[gaDisableKey: `ga-disable-${string}`]: boolean | undefined;
	}
}

const SCRIPT_ID = "google-analytics";

let measurementId = "";

/** Pushes to the same array the GA snippet uses, so calls made before the script lands are not lost. */
const gtag = (...args: GtagArguments): void => {
	window.dataLayer = window.dataLayer ?? [];
	window.dataLayer.push(args);
};

/**
 * Injects the GA4 script once. A missing or empty measurement id is a normal state — local
 * development and any build that has not been given one stay completely untracked, with no script
 * request and no storage written. Only called once the visitor has accepted analytics cookies
 * (see cookieConsent.ts); until then nothing is loaded at all.
 */
export const initGoogleAnalytics = (id: string): void => {
	if (!id) {
		return;
	}

	// Re-accepting after a Reject in the same page: the script is already there, just switch it back on.
	window[`ga-disable-${id}`] = false;

	if (measurementId || document.getElementById(SCRIPT_ID)) {
		return;
	}

	measurementId = id;

	const script = document.createElement("script");
	script.async = true;
	script.id = SCRIPT_ID;
	script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`;
	document.head.appendChild(script);

	window.gtag = gtag;
	gtag("js", new Date());

	// Automatic page views are turned off because this is a client-routed SPA: GA would report one
	// view for the whole session. trackPageView below is called on each route change instead.
	gtag("config", id, { send_page_view: false });
};

/**
 * Stops GA4 for a visitor who withdrew consent: sets GA's own opt-out flag so the loaded script sends
 * nothing more, and removes the _ga cookies it wrote on this site.
 */
export const disableGoogleAnalytics = (id: string): void => {
	if (!id) {
		return;
	}

	window[`ga-disable-${id}`] = true;
	measurementId = "";

	const host = window.location.hostname;
	const domains = ["", host, `.${host.replace(/^www\./, "")}`];
	document.cookie
		.split(";")
		.map((entry: string) => entry.trim().split("=")[0])
		.filter((name: string) => name === "_ga" || name.startsWith("_ga_"))
		.forEach((name: string) =>
			domains.forEach((domain: string) => {
				document.cookie = `${name}=; Max-Age=0; path=/${domain ? `; domain=${domain}` : ""}`;
			})
		);
};

/**
 * Reports one page view. Called on every route change from the single mounted tracker, never from
 * a page component.
 */
export const trackPageView = (path: string): void => {
	if (!measurementId) {
		return;
	}

	gtag("event", "page_view", {
		page_location: window.location.origin + path,
		page_path: path,
		page_title: document.title
	});
};
